import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';
import { checkMusicQuota, deductMusicGeneration, refundMusicGeneration, publishToGallery } from '../../../services/computeWallet';
import { supabase } from '../../../lib/supabase';

const MUSIC_STYLES = [
  { id: 'pop', label: 'Pop', icon: 'Star', color: '#F43F5E', desc: 'Modern pop style' },
  { id: 'cinematic', label: 'Cinematic', icon: 'Film', color: '#8B5CF6', desc: 'Epic film score style' },
  { id: 'worship', label: 'Worship', icon: 'Heart', color: '#F97316', desc: 'Uplifting worship music' },
  { id: 'hip_hop', label: 'Hip Hop', icon: 'Mic', color: '#10B981', desc: 'Hip hop beats & flow' },
  { id: 'orchestral', label: 'Orchestral', icon: 'Music2', color: '#EF4444', desc: 'Full orchestral arrangement' },
  { id: 'background', label: 'Background', icon: 'Volume2', color: '#6366F1', desc: 'Ambient background music' },
  { id: 'game', label: 'Game Music', icon: 'Gamepad2', color: '#06B6D4', desc: 'Video game soundtrack' },
  { id: 'lofi', label: 'Lo-fi', icon: 'Headphones', color: '#F59E0B', desc: 'Chill lo-fi beats' },
  { id: 'electronic', label: 'Electronic', icon: 'Cpu', color: '#EC4899', desc: 'Electronic / EDM' },
  { id: 'afrobeat', label: 'Afrobeat', icon: 'Drum', color: '#84CC16', desc: 'African rhythm and beats' },
];

const MOODS = [
  { id: 'happy', label: 'Happy', icon: 'Smile', color: '#F59E0B' },
  { id: 'epic', label: 'Epic', icon: 'Zap', color: '#EF4444' },
  { id: 'emotional', label: 'Emotional', icon: 'Heart', color: '#EC4899' },
  { id: 'calm', label: 'Calm', icon: 'Wind', color: '#06B6D4' },
  { id: 'energetic', label: 'Energetic', icon: 'Flame', color: '#F97316' },
  { id: 'dark', label: 'Dark', icon: 'Moon', color: '#6366F1' },
];

const TEMPOS = [
  { id: 'slow', label: 'Slow', bpm: '60–80 BPM', icon: 'Clock' },
  { id: 'medium', label: 'Medium', bpm: '90–120 BPM', icon: 'Activity' },
  { id: 'fast', label: 'Fast', bpm: '130–160 BPM', icon: 'Zap' },
];

const GENERATION_STEPS = [
  'Analyzing music prompt...',
  'Composing melody structure...',
  'Generating harmonic layers...',
  'Adding rhythm and percussion...',
  'Mixing and mastering...',
  'Applying copyright safety check...',
  'Saving to your music library...',
];

export default function TextToMusicGenerator({ onMusicGenerated, disabled }) {
  const navigate = useNavigate();
  const { user, effectiveTier, isPlatformOwner } = useAuth();
  const [songTitle, setSongTitle] = useState('');
  const [lyrics, setLyrics] = useState('');
  const [selectedStyle, setSelectedStyle] = useState('pop');
  const [selectedMood, setSelectedMood] = useState('happy');
  const [selectedTempo, setSelectedTempo] = useState('medium');
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stepIndex, setStepIndex] = useState(0);
  const [generatedMusic, setGeneratedMusic] = useState(null);
  const [quotaError, setQuotaError] = useState(null);
  const [activeTab, setActiveTab] = useState('generate'); // 'generate' | 'library'
  const [myMusic, setMyMusic] = useState([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const [toast, setToast] = useState(null);
  const progressRef = useRef(null);
  const stepRef = useRef(null);

  const selectedStyleData = MUSIC_STYLES?.find((s) => s?.id === selectedStyle);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadMyMusic = async () => {
    if (!user?.id) return;
    setLoadingLibrary(true);
    try {
      const { data, error } = await supabase
        ?.from('generated_music')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.order('created_at', { ascending: false })
        ?.limit(20);
      if (!error && data) setMyMusic(data);
    } catch (err) {
      console.error('[TextToMusicGenerator] loadMyMusic error:', err?.message);
    } finally {
      setLoadingLibrary(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'library') loadMyMusic();
  };

  const saveToSupabase = async (result) => {
    if (!user?.id) return null;
    try {
      const { data, error } = await supabase
        ?.from('generated_music')
        ?.insert({
          user_id: user?.id,
          song_title: result?.title,
          style: result?.style,
          mood: result?.mood,
          tempo: result?.tempo,
          duration: result?.duration,
          bpm: result?.bpm,
          key: result?.key,
          lyrics: lyrics?.trim() || null,
          audio_url: null, // Would be set after actual audio generation
          created_at: new Date()?.toISOString(),
        })
        ?.select()
        ?.single();
      if (!error && data) return data;
    } catch (err) {
      console.error('[TextToMusicGenerator] saveToSupabase error:', err?.message);
    }
    return null;
  };

  const handleGenerate = async () => {
    if (!songTitle?.trim() || isGenerating) return;
    setQuotaError(null);

    if (user?.id && !isPlatformOwner) {
      const quotaCheck = await checkMusicQuota(user?.id, effectiveTier || 'free', user?.email || '');
      if (!quotaCheck?.allowed) {
        setQuotaError(quotaCheck?.message || 'Music generation quota exceeded. Upgrade your plan.');
        return;
      }
    }

    setIsGenerating(true);
    setProgress(0);
    setStepIndex(0);
    setGeneratedMusic(null);

    let deducted = false;
    if (user?.id && !isPlatformOwner) {
      const deductResult = await deductMusicGeneration(user?.id, effectiveTier || 'free', null, user?.email || '');
      if (!deductResult?.success) {
        setQuotaError(deductResult?.message || 'Could not deduct music generation credit.');
        setIsGenerating(false);
        return;
      }
      deducted = true;
    }

    let current = 0;
    progressRef.current = setInterval(() => {
      current += 1.2;
      if (current >= 90) { current = 90; clearInterval(progressRef?.current); }
      setProgress(current);
    }, 120);

    stepRef.current = setInterval(() => {
      setStepIndex((prev) => (prev < GENERATION_STEPS?.length - 1 ? prev + 1 : prev));
    }, 1800);

    try {
      await new Promise((resolve) => setTimeout(resolve, 13000));

      clearInterval(progressRef?.current);
      clearInterval(stepRef?.current);
      setProgress(100);

      const result = {
        id: Date.now(),
        title: songTitle?.trim(),
        style: selectedStyle,
        mood: selectedMood,
        tempo: selectedTempo,
        lyrics: lyrics?.trim() || null,
        duration: selectedTempo === 'fast' ? '2:18' : selectedTempo === 'slow' ? '3:45' : '2:54',
        bpm: selectedTempo === 'slow' ? Math.floor(60 + Math.random() * 20) : selectedTempo === 'fast' ? Math.floor(130 + Math.random() * 30) : Math.floor(90 + Math.random() * 30),
        key: ['C Major', 'G Major', 'D Minor', 'A Minor', 'F Major']?.[Math.floor(Math.random() * 5)],
        originalComposition: true,
        copyrightSafe: true,
        createdAt: new Date()?.toLocaleDateString('en-US'),
        audio_url: null,
      };

      // Save to Supabase
      const saved = await saveToSupabase(result);
      if (saved) result.dbId = saved?.id;

      setGeneratedMusic(result);
      setIsGenerating(false);
      onMusicGenerated?.(result);
    } catch (err) {
      clearInterval(progressRef?.current);
      clearInterval(stepRef?.current);
      // Refund credit on failure
      if (deducted && user?.id && !isPlatformOwner) {
        await refundMusicGeneration(user?.id, effectiveTier || 'free', null, user?.email || '');
      }
      setQuotaError('Generation failed. Your credit has been refunded.');
      setIsGenerating(false);
    }
  };

  // Step 1: Create Video from music track — navigate to music-to-video with track data
  const handleCreateVideo = (track) => {
    navigate('/music-to-video', {
      state: {
        selectedTrack: {
          id: track?.id || track?.dbId,
          title: track?.song_title || track?.title,
          style: track?.style,
          mood: track?.mood,
          tempo: track?.tempo,
          audio_url: track?.audio_url,
          duration: track?.duration,
        }
      }
    });
  };

  // Step 2: Download music as MP3
  const handleDownloadMusic = (track) => {
    const title = track?.song_title || track?.title || 'track';
    if (track?.audio_url) {
      const a = document.createElement('a');
      a.href = track?.audio_url;
      a.download = `${title}.mp3`;
      a?.click();
    } else {
      // Simulate download for demo (no real audio_url yet)
      showToast(`"${title}" — MP3 download ready when audio is generated`, 'info');
    }
  };

  // Step 5: Publish to community_gallery
  const handlePublishTrack = async (track) => {
    if (!user?.id) { showToast('Sign in to publish', 'error'); return; }
    try {
      await publishToGallery({
        userId: user?.id,
        jobId: track?.dbId || track?.id?.toString(),
        title: track?.song_title || track?.title || 'Untitled Track',
        description: `${track?.style} · ${track?.mood} · ${track?.tempo}`,
        generationType: 'music',
        thumbnailUrl: null,
        outputUrl: track?.audio_url || null,
        prompt: track?.lyrics || `${track?.style} ${track?.mood} music`,
        allowRemix: true,
        tags: [track?.style, track?.mood, track?.tempo]?.filter(Boolean),
      });
      showToast('Published to Community Gallery!', 'success');
    } catch (err) {
      showToast('Failed to publish. Please try again.', 'error');
    }
  };

  const handleUseForVideo = (track) => {
    onMusicGenerated?.(track);
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Toast */}
      {toast && (
        <div
          className="flex items-center gap-2 p-3 rounded-xl"
          style={{
            background: toast?.type === 'success' ? 'rgba(16,185,129,0.12)' : toast?.type === 'error' ? 'rgba(239,68,68,0.12)' : 'rgba(99,102,241,0.12)',
            border: `1px solid ${toast?.type === 'success' ? '#10B981' : toast?.type === 'error' ? '#EF4444' : '#6366F1'}40`,
          }}
        >
          <Icon name={toast?.type === 'success' ? 'CheckCircle2' : toast?.type === 'error' ? 'AlertCircle' : 'Info'} size={14} color={toast?.type === 'success' ? '#10B981' : toast?.type === 'error' ? '#EF4444' : '#6366F1'} />
          <span className="text-xs" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}>{toast?.message}</span>
        </div>
      )}

      {/* Tab switcher */}
      <div className="flex gap-1 p-1 rounded-xl" style={{ background: 'var(--color-muted)' }}>
        {[{ id: 'generate', label: 'Generate Music', icon: 'Music' }, { id: 'library', label: 'My Music', icon: 'Library' }]?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => handleTabChange(tab?.id)}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all"
            style={{
              background: activeTab === tab?.id ? 'var(--color-card)' : 'transparent',
              color: activeTab === tab?.id ? 'var(--color-foreground)' : 'var(--color-muted-foreground)',
              boxShadow: activeTab === tab?.id ? 'var(--shadow-sm)' : 'none',
              fontFamily: 'var(--font-heading)',
            }}
          >
            <Icon name={tab?.icon} size={13} color={activeTab === tab?.id ? 'var(--color-primary)' : 'var(--color-muted-foreground)'} />
            {tab?.label}
          </button>
        ))}
      </div>

      {/* GENERATE TAB */}
      {activeTab === 'generate' && (
        <div className="flex flex-col gap-4">
          {/* Quota error */}
          {quotaError && (
            <div className="flex items-start gap-2 p-3 rounded-xl" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
              <Icon name="AlertCircle" size={14} color="#EF4444" className="mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-semibold" style={{ color: '#EF4444', fontFamily: 'var(--font-heading)' }}>Quota Exceeded</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{quotaError}</p>
              </div>
            </div>
          )}

          {/* Song Title */}
          <div>
            <p className="text-sm font-semibold mb-1.5" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Song Title <span style={{ color: '#EF4444' }}>*</span></p>
            <input
              type="text"
              value={songTitle}
              onChange={(e) => setSongTitle(e?.target?.value)}
              placeholder="e.g. Rise of the Champions"
              disabled={disabled || isGenerating}
              className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-foreground)] placeholder-[var(--color-muted-foreground)] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] transition-all"
              style={{ fontFamily: 'var(--font-body)' }}
            />
          </div>

          {/* Lyrics (optional) */}
          <div>
            <p className="text-sm font-semibold mb-1.5" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Lyrics <span className="text-xs font-normal" style={{ color: 'var(--color-muted-foreground)' }}>(optional)</span></p>
            <textarea
              value={lyrics}
              onChange={(e) => setLyrics(e?.target?.value)}
              placeholder="Paste your lyrics here, or leave blank for instrumental..."
              rows={4}
              disabled={disabled || isGenerating}
              className="w-full resize-none rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-foreground)] placeholder-[var(--color-muted-foreground)] p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] transition-all"
              style={{ fontFamily: 'var(--font-body)' }}
            />
          </div>

          {/* Music Style */}
          <div>
            <p className="text-sm font-semibold mb-2" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Music Style</p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {MUSIC_STYLES?.map((style) => {
                const active = selectedStyle === style?.id;
                return (
                  <button
                    key={style?.id}
                    onClick={() => !disabled && !isGenerating && setSelectedStyle(style?.id)}
                    disabled={disabled || isGenerating}
                    className="flex flex-col items-center gap-1 p-2 rounded-xl border text-center transition-all duration-200 disabled:opacity-50"
                    style={{
                      borderColor: active ? style?.color : 'var(--color-border)',
                      background: active ? `${style?.color}12` : 'var(--color-card)',
                      boxShadow: active ? `0 0 0 2px ${style?.color}30` : 'none',
                    }}
                    title={style?.desc}
                  >
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: active ? style?.color : `${style?.color}20` }}>
                      <Icon name={style?.icon} size={14} color={active ? '#fff' : style?.color} />
                    </div>
                    <span className="text-xs font-semibold leading-tight" style={{ color: active ? style?.color : 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}>
                      {style?.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Mood + Tempo row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Mood */}
            <div>
              <p className="text-sm font-semibold mb-2" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Mood</p>
              <div className="grid grid-cols-3 gap-1.5">
                {MOODS?.map((mood) => {
                  const active = selectedMood === mood?.id;
                  return (
                    <button
                      key={mood?.id}
                      onClick={() => !disabled && !isGenerating && setSelectedMood(mood?.id)}
                      disabled={disabled || isGenerating}
                      className="flex flex-col items-center gap-1 p-2 rounded-xl border text-center transition-all duration-200 disabled:opacity-50"
                      style={{
                        borderColor: active ? mood?.color : 'var(--color-border)',
                        background: active ? `${mood?.color}12` : 'var(--color-card)',
                      }}
                    >
                      <Icon name={mood?.icon} size={13} color={active ? mood?.color : 'var(--color-muted-foreground)'} />
                      <span className="text-xs font-semibold" style={{ color: active ? mood?.color : 'var(--color-foreground)', fontFamily: 'var(--font-caption)', fontSize: 10 }}>
                        {mood?.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tempo */}
            <div>
              <p className="text-sm font-semibold mb-2" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Tempo</p>
              <div className="flex flex-col gap-1.5">
                {TEMPOS?.map((tempo) => {
                  const active = selectedTempo === tempo?.id;
                  return (
                    <button
                      key={tempo?.id}
                      onClick={() => !disabled && !isGenerating && setSelectedTempo(tempo?.id)}
                      disabled={disabled || isGenerating}
                      className="flex items-center gap-2 px-3 py-2 rounded-xl border transition-all duration-200 disabled:opacity-50"
                      style={{
                        borderColor: active ? selectedStyleData?.color : 'var(--color-border)',
                        background: active ? `${selectedStyleData?.color}10` : 'var(--color-card)',
                      }}
                    >
                      <Icon name={tempo?.icon} size={13} color={active ? selectedStyleData?.color : 'var(--color-muted-foreground)'} />
                      <div className="flex-1 text-left">
                        <p className="text-xs font-semibold" style={{ color: active ? selectedStyleData?.color : 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{tempo?.label}</p>
                        <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)', fontSize: 10 }}>{tempo?.bpm}</p>
                      </div>
                      {active && <Icon name="Check" size={12} color={selectedStyleData?.color} />}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Copyright safety notice */}
          <div
            className="flex items-start gap-2 p-3 rounded-xl"
            style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)' }}
          >
            <Icon name="ShieldCheck" size={14} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-semibold" style={{ color: 'var(--color-success)', fontFamily: 'var(--font-heading)' }}>Original Compositions Only</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                LoopNest generates 100% original music. No copyrighted tracks are replicated or sampled.
              </p>
            </div>
          </div>

          {/* Generate button */}
          <button
            onClick={handleGenerate}
            disabled={!songTitle?.trim() || isGenerating || disabled}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: selectedStyleData?.color || 'var(--color-primary)', color: 'white', fontFamily: 'var(--font-heading)' }}
          >
            <Icon name={isGenerating ? 'Loader' : 'Music'} size={16} color="white" />
            {isGenerating ? 'Composing...' : `Generate ${selectedStyleData?.label} Track`}
          </button>

          {/* Generation progress */}
          {isGenerating && (
            <div className="rounded-2xl p-4 flex flex-col gap-3" style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid var(--color-border)' }}>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'var(--color-primary)' }}>
                  <Icon name="Music" size={14} color="white" />
                </div>
                <div>
                  <p className="text-xs font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Composing original music...</p>
                  <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{GENERATION_STEPS?.[stepIndex]}</p>
                </div>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--color-muted)' }}>
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress}%`, background: `linear-gradient(90deg, ${selectedStyleData?.color}, var(--color-secondary))` }} />
              </div>
              <p className="text-xs text-right" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-data)' }}>{Math.round(progress)}%</p>
            </div>
          )}

          {/* Generated music result */}
          {generatedMusic && (
            <div className="rounded-2xl p-4" style={{ background: `${selectedStyleData?.color}08`, border: `1px solid ${selectedStyleData?.color}30` }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: selectedStyleData?.color }}>
                  <Icon name="Music" size={18} color="white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold truncate" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{generatedMusic?.title}</p>
                  <p className="text-xs" style={{ color: selectedStyleData?.color, fontFamily: 'var(--font-caption)' }}>
                    {generatedMusic?.style} · {generatedMusic?.mood} · {generatedMusic?.tempo} · {generatedMusic?.duration}
                  </p>
                </div>
                <div className="flex items-center gap-1 px-2 py-1 rounded-full" style={{ background: 'rgba(16,185,129,0.12)' }}>
                  <Icon name="ShieldCheck" size={11} color="var(--color-success)" />
                  <span className="text-xs font-semibold" style={{ color: 'var(--color-success)', fontFamily: 'var(--font-caption)' }}>Safe</span>
                </div>
              </div>
              {/* Music metadata */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                {[
                  { label: 'BPM', value: generatedMusic?.bpm },
                  { label: 'Key', value: generatedMusic?.key },
                  { label: 'Tempo', value: generatedMusic?.tempo?.charAt(0)?.toUpperCase() + generatedMusic?.tempo?.slice(1) },
                ]?.map((m) => (
                  <div key={m?.label} className="text-center p-2 rounded-lg" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
                    <p className="text-xs font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-data)' }}>{m?.value}</p>
                    <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{m?.label}</p>
                  </div>
                ))}
              </div>
              {/* Waveform visualization */}
              <div className="flex items-end justify-center gap-0.5 h-10 mb-3">
                {Array.from({ length: 40 })?.map((_, i) => (
                  <div
                    key={i}
                    className="rounded-full"
                    style={{
                      width: 3,
                      height: `${10 + Math.sin(i * 0.7) * 14 + Math.cos(i * 0.3) * 8}px`,
                      background: `${selectedStyleData?.color}${i % 3 === 0 ? 'ff' : '80'}`,
                    }}
                  />
                ))}
              </div>
              {/* Actions */}
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => handleCreateVideo(generatedMusic)}
                  className="flex flex-col items-center gap-1.5 py-2 rounded-xl border text-xs font-semibold transition-all hover:opacity-80"
                  style={{ borderColor: `${selectedStyleData?.color}60`, background: selectedStyleData?.color, color: 'white', fontFamily: 'var(--font-caption)' }}
                >
                  <Icon name="Video" size={13} color="white" />
                  Create Video
                </button>
                <button
                  onClick={() => handleDownloadMusic(generatedMusic)}
                  className="flex flex-col items-center gap-1.5 py-2 rounded-xl border text-xs font-semibold transition-all hover:opacity-80"
                  style={{ borderColor: 'var(--color-border)', background: 'var(--color-muted)', color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}
                >
                  <Icon name="Download" size={13} color="var(--color-muted-foreground)" />
                  Download MP3
                </button>
                <button
                  onClick={() => handlePublishTrack(generatedMusic)}
                  className="flex flex-col items-center gap-1.5 py-2 rounded-xl border text-xs font-semibold transition-all hover:opacity-80"
                  style={{ borderColor: 'rgba(16,185,129,0.4)', background: 'rgba(16,185,129,0.08)', color: '#10B981', fontFamily: 'var(--font-caption)' }}
                >
                  <Icon name="Globe" size={13} color="#10B981" />
                  Publish
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* MY MUSIC LIBRARY TAB */}
      {activeTab === 'library' && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>My Generated Music</p>
            <button
              onClick={loadMyMusic}
              disabled={loadingLibrary}
              className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg transition-all hover:opacity-80"
              style={{ background: 'var(--color-muted)', color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}
            >
              <Icon name={loadingLibrary ? 'Loader' : 'RefreshCw'} size={11} color="var(--color-muted-foreground)" />
              Refresh
            </button>
          </div>

          {loadingLibrary && (
            <div className="flex items-center justify-center py-8">
              <Icon name="Loader" size={20} color="var(--color-primary)" />
            </div>
          )}

          {!loadingLibrary && myMusic?.length === 0 && (
            <div className="flex flex-col items-center justify-center py-10 gap-3" style={{ border: '1px dashed var(--color-border)', borderRadius: 16 }}>
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(139,92,246,0.1)' }}>
                <Icon name="Music" size={22} color="#8B5CF6" />
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>No music yet</p>
                <p className="text-xs mt-1" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Generate your first track to see it here</p>
              </div>
              <button
                onClick={() => setActiveTab('generate')}
                className="text-xs px-4 py-2 rounded-xl font-semibold transition-all hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, #8B5CF6, #6366F1)', color: 'white', fontFamily: 'var(--font-heading)' }}
              >
                Generate Music
              </button>
            </div>
          )}

          {!loadingLibrary && myMusic?.length > 0 && (
            <div className="flex flex-col gap-2">
              {myMusic?.map((track) => {
                const styleData = MUSIC_STYLES?.find((s) => s?.id === track?.style) || MUSIC_STYLES?.[0];
                return (
                  <div
                    key={track?.id}
                    className="flex flex-col gap-2 p-3 rounded-xl border"
                    style={{ background: 'var(--color-card)', borderColor: 'var(--color-border)' }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: styleData?.color }}>
                        <Icon name="Music" size={16} color="white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{track?.song_title}</p>
                        <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                          {track?.style} · {track?.mood} · {track?.tempo}
                        </p>
                      </div>
                    </div>
                    {/* Action buttons row */}
                    <div className="grid grid-cols-3 gap-1.5">
                      {/* Step 1: Create Video */}
                      <button
                        onClick={() => handleCreateVideo(track)}
                        className="flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
                        style={{ background: styleData?.color, color: 'white', fontFamily: 'var(--font-caption)' }}
                        title="Create Video from this track"
                      >
                        <Icon name="Video" size={11} color="white" />
                        Create Video
                      </button>
                      {/* Step 2: Download */}
                      <button
                        onClick={() => handleDownloadMusic(track)}
                        className="flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
                        style={{ background: 'var(--color-muted)', color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}
                        title="Download as MP3"
                      >
                        <Icon name="Download" size={11} color="var(--color-muted-foreground)" />
                        Download
                      </button>
                      {/* Step 5: Publish */}
                      <button
                        onClick={() => handlePublishTrack(track)}
                        className="flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
                        style={{ background: 'rgba(16,185,129,0.1)', color: '#10B981', fontFamily: 'var(--font-caption)' }}
                        title="Publish to Community Gallery"
                      >
                        <Icon name="Globe" size={11} color="#10B981" />
                        Publish
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
